import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field, FieldProps, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { Button, Grid, Header, Modal } from 'semantic-ui-react';
import { required } from 'utils/validatorUtils';
import Input from 'components/final-form/Input';
import { RoleDto } from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import { Privilege } from 'ts-types/api.enums';
import { addRole, editRole } from 'services/roleServices';
import RolePrivilegesSegment from 'components/user-settings/segments/RolePrivilegesSegment';
import { errorUtils } from 'utils/errorUtils';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';

interface Props extends MessageSourceProps {
  onCancel: () => void,
  onSave: () => void,
  modalOpen: boolean,
  roleId?: string,
  role?: RoleDto
}

interface State {
  role?: Partial<RoleDto>,
  selectedPrivileges: Array<Privilege>,
  privileges: Array<Privilege>,
  availablePrivileges: Array<Privilege>,
  errorMessage?: string,
  cancelTokenSource: CancelTokenSource
}

const VALIDATED_FIELDS: Array<string> = [
  "roleId",
  "name",
  "description"
];

const initialRole: Partial<RoleDto> = {
  roleId: "",
  name: "",
  description: ""
};

class UpsertRoleForm extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {role} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    const privileges: Array<Privilege> = Object.values(Privilege);

    const selectedPrivileges: Array<Privilege> = role
        ? privileges.filter(privilege => role.privileges.includes(privilege))
        : [];

    const availablePrivileges: Array<Privilege> = role
        ? privileges.filter(privilege => !role.privileges.includes(privilege))
        : privileges;

    this.state = {
      selectedPrivileges: selectedPrivileges,
      privileges: privileges,
      availablePrivileges: availablePrivileges,
      cancelTokenSource
    };
  }

  setSelectedPrivileges = (selectedPrivileges: Array<Privilege>) => {
    this.setState({
      selectedPrivileges: selectedPrivileges
    })
  };

  handleSubmit = (values: Partial<RoleDto>): void => {

    const {roleId, onSave} = this.props;
    const {selectedPrivileges, cancelTokenSource} = this.state;

    values.privileges = selectedPrivileges;

    if (roleId) {
      editRole(roleId, values, cancelTokenSource)
          .then(onSave)
          .catch((error) => this.handleError(error))
    } else {
      addRole(values, cancelTokenSource)
          .then(onSave)
          .catch((error) => this.handleError(error))
    }
  };

  handleError(error: any) {
    const {getMessage} = this.props;

    const knownErrors = [errorUtils.existingRole];
    let errorMessage = "";

    let specificError = undefined;
    if (error.response.data && error.response.data.violations) {
      specificError = error.response.data.violations
          .find((violation: any) => knownErrors.includes(violation.errorCode));
    }

    if (specificError) {
      errorMessage = getMessage(`error.${specificError.errorCode}`)
    } else {
      errorMessage = getMessage('error.general');
    }

    this.setErrorMessage(errorMessage)
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {modalOpen, onCancel, role = initialRole, getMessage} = this.props;

    return (
        <Modal
            open={modalOpen}
            onClose={onCancel}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("header.navigation.roles")}</Modal.Header>
          <FormModalContentContainer>
            <FinalForm
                onSubmit={(values) => this.handleSubmit(values)}
                initialValues={role}
                subscription={{pristine: true}}
                render={this.renderForm}
            />
          </FormModalContentContainer>
        </Modal>
    )
  };

  renderForm = ({handleSubmit, submitting}: FormRenderProps): React.ReactNode => {

    const {getMessage, onCancel} = this.props;
    const {selectedPrivileges, availablePrivileges, privileges, errorMessage} = this.state;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
              && (
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
              )
              }
              <Grid.Row>
                <Grid.Column width={16}>
                  {this.renderBasicDataSection()}
                </Grid.Column>
              </Grid.Row>
              <Grid.Row style={{marginTop: "1rem"}}>
                <Grid.Column width={16} stretched>
                  <RolePrivilegesSegment
                      privileges={privileges}
                      selectedPrivileges={selectedPrivileges && selectedPrivileges.length > 0 ? selectedPrivileges : []}
                      availablePrivileges={availablePrivileges}
                      setSelectedPrivileges={this.setSelectedPrivileges}
                  />
                </Grid.Column>
              </Grid.Row>
            </FormContent>

            <FormButtonsRow>
              <Button compact type="submit" primary disabled={submitting}>{getMessage("button.save")}</Button>
              <Button compact type="button" disabled={submitting}
                      onClick={onCancel}>{getMessage("button.cancel")}</Button>
            </FormButtonsRow>
          </FormContainer>
        </form>
    )
        ;
  };

  renderBasicDataSection = (): React.ReactNode => {

    const {roleId, getMessage} = this.props;

    const descriptionRows: Array<Array<string>> = [
      ["roleId"],
      ["name"],
      ["description"]
    ];

    const validator = (value: string) => required(value);
    const descriptionSection = descriptionRows.map((row, index) => {
      return (
          <Grid.Row key={index}>
            {
              row.map((field, index1) => {
                let fieldProps: FieldProps = {
                  name: field,
                  validate: VALIDATED_FIELDS.includes(field) ? validator : undefined,
                  component: Input
                };

                return (
                    <React.Fragment key={index1}>
                      <Grid.Column width={5} verticalAlign="middle">

                        {
                          field === "description" || "name" ?
                              `${getMessage(field)}:` :
                              `${getMessage(`${field}`)}:`
                        }
                      </Grid.Column>
                      <Grid.Column width={11} verticalAlign="middle">
                        <Field
                            fluid
                            {...fieldProps}
                            disabled={field === "roleId" && roleId !== undefined}
                            size="mini"
                        />
                      </Grid.Column>
                    </React.Fragment>
                );
              })
            }
          </Grid.Row>
      );
    });

    return (
        <PraxFormGrid>
          <Grid.Row>
            <Grid.Column width={16}>
              <Header as='h5' style={{marginTop: "1rem"}}>
                {getMessage("description")}
              </Header>
            </Grid.Column>
          </Grid.Row>
          {descriptionSection}
        </PraxFormGrid>
    );
  };
}

export default withMessages(UpsertRoleForm);
